import { useState } from "react";
import { GigApiPaginatedData } from "./common/apiResponse";
import { GigApiFetcherResponse, useAuthenticatedGigApiFetcher } from "./common/fetching";
import { useGiggedApiSWR, UseGiggedApiSWRReturn } from "./common/giggedApiSWR";
import { SortOrder } from "./common/sortOrder";
import { CreateGigDto, GigOrderByFieldKeys, GigSummary } from "../models/app/gig";
import { ApiCreateGigDto, ApiGigSummary } from "../models/api/gig";
import { mapFromApiGigSummary, mapToApiGig } from "../models/mappers/gig";
import { useStateParams } from "../hooks/useStateParams";
import { ITEMS_PER_PAGE } from "./common/pagination";

const useCreateGig = () => useAuthenticatedGigApiFetcher<ApiCreateGigDto, string, CreateGigDto>("POST",
    (dto) => ({
        url: "api/me/gigs",
        body: mapToApiGig(dto),
    }));

export type UseGigsParams = {
    initialPageSize?: number
    gigStatusIds?: number[]
    initialOrderBy?: GigOrderByFieldKeys
    initialSortOrder?: SortOrder
    giggedClientId?: string
    pauseFetching?: boolean
    allClients?: boolean
    hiredTalentId?: string
}

export type UseGigsReturn =
    Omit<UseGiggedApiSWRReturn<GigApiPaginatedData<ApiGigSummary>>, "data"> &
    Partial<Omit<UseGiggedApiSWRReturn<GigApiPaginatedData<ApiGigSummary>>["data"], "items">> &
    {
        gigs: GigSummary[]
        sortOrder: SortOrder
        orderBy: GigOrderByFieldKeys
        skillIds: string[]
        gigTaggedUserIds: string[]
        expectedDurationIds: number[]
        searchTerm: string
        referenceNumber: string
        gigTitle: string
        inviteCreatedBy: string
        startDate: string
        hasClientHistory: string
        locationIds: string[]
        localityIds: string[]
        deliveryTypeIds: number[]
        createdBy: string
        talentName: string
        pageIndex: number
        pageSize: number
        totalCount: number
        totalPages: number
        hasPreviousPage: boolean
        hasNextPage: boolean
        setSearchTerm: (value: string) => void
        setReferenceNumber: (value: string) => void
        setGigTitle: (value: string) => void
        setSkillIds: (skillIds: string[]) => void
        setTaggedIds: (gigTaggedUserIds: string[]) => void
        setExpectedDurationIds: (expectedDurationIds: number[]) => void
        setSortOrder: (value: SortOrder) => void
        setOrderBy: (value: GigOrderByFieldKeys) => void
        setPageIndex: (value: number) => void
        setPageSize: (value: number) => void
        setHasClientHistory: (value: string) => void
        setLocationIds: (locationIds: string[]) => void
        setLocalityIds: (localityIds: string[]) => void
        setDeliveryTypeIds: (deliveryTypeIds: number[]) => void
        setCreatedBy: (value: string) => void
        setInviteCreatedBy: (value: string) => void
        setTalentName: (value: string) => void
        setStartDate: (value: string) => void
        refetchGigs: () => void
        createGig: (dto: CreateGigDto) => Promise<GigApiFetcherResponse<string>>
        isCreatingGig: boolean,
        updateLocalGig: (gigId: string, updates: Partial<GigSummary>) => void;
    }

export type UseGigs = (params?: UseGigsParams) => UseGigsReturn;

export const useGigs: UseGigs = ({
    initialPageSize,
    gigStatusIds,
    initialOrderBy,
    initialSortOrder,
    giggedClientId,
    pauseFetching,
    allClients,
    hiredTalentId,
} = {}) => {
    const [searchTerm, setSearchTerm] = useStateParams("", "searchTerm", "string");
    const [referenceNumber, setReferenceNumber] = useStateParams("", "referenceNumber", "string");
    const [gigTitle, setGigTitle] = useStateParams("", "gigTitle", "string");
    const [skillIds, setSkillIds] = useStateParams<string[]>([], "skillIds", "stringArray");
    const [gigTaggedUserIds, setTaggedIds] = useStateParams<string[]>([], "gigTaggedUserIds", "stringArray");
    const [expectedDurationIds, setExpectedDurationIds] = useStateParams<number[]>([], "expectedDurationIds", "stringArray");
    const [hasClientHistory, setHasClientHistory] = useStateParams<string>("", "hasClientHistory", "string");
    const [locationIds, setLocationIds] = useStateParams<string[]>([], "locationIds", "stringArray");
    const [localityIds, setLocalityIds] = useStateParams<string[]>([], "localityIds", "stringArray");
    const [deliveryTypeIds, setDeliveryTypeIds] = useStateParams<number[]>([], "deliveryTypeIds", "stringArray");
    const [createdBy, setCreatedBy] = useStateParams<string>("", "createdBy", "string");
    const [inviteCreatedBy, setInviteCreatedBy] = useStateParams<string>("", "inviteCreatedBy", "string");
    const [talentName, setTalentName] = useStateParams<string>("", "talentName", "string");
    const [startDate, setStartDate] = useStateParams<string>("", "startDate", "string");
    const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder || "asc");
    const [orderBy, setOrderBy] = useState<GigOrderByFieldKeys>(initialOrderBy || "Title");
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(initialPageSize || ITEMS_PER_PAGE);

    const searchTermQuery = searchTerm && searchTerm !== "" ? `&searchTerm=${searchTerm}` : "";
    const referenceNumberQuery = referenceNumber && referenceNumber !== "" ? `&referenceNumber=${referenceNumber}` : "";
    const gigTitleQuery = gigTitle && gigTitle !== "" ? `&gigTitle=${gigTitle}` : "";
    const talentNameQuery = talentName && talentName !== "" ? `&talentName=${talentName}` : "";
    const startDateQuery = startDate && startDate !== "" ? `&startDate=${startDate}` : "";
    const skillsQuery = skillIds.length > 0 ? `&skillIds=${skillIds.join(",")}` : "";
    const taggedQuery = gigTaggedUserIds.length > 0 ? `&gigTaggedUserIds=${gigTaggedUserIds.join(",")}` : "";
    const expectedDurationsQuery = expectedDurationIds.length > 0 ? `&expectedDurationIds=${expectedDurationIds.join(",")}` : "";
    const gigStatusQuery = gigStatusIds && gigStatusIds.length > 0 ? `&gigStatusIds=${gigStatusIds?.join(",")}` : "";
    const hasClientHistoryQuery = hasClientHistory && hasClientHistory !== "" ? `&hasClientHistory=${hasClientHistory}` : "";
    const locationsQuery = locationIds.length > 0 ? `&locationIds=${locationIds.join(",")}` : "";
    const localitiesQuery = localityIds.length > 0 ? `&localityIds=${localityIds.join(",")}` : "";
    const deliveryTypesQuery = deliveryTypeIds.length > 0 ? `&deliveryTypeIds=${deliveryTypeIds.join(",")}` : "";
    const createdByQuery = createdBy && createdBy !== "" ? `&createdBy=${createdBy}` : "";
    const inviteCreatedByQuery = inviteCreatedBy && inviteCreatedBy !== "" ? `&inviteCreatedBy=${inviteCreatedBy}` : "";

    const url = `api/${allClients ? "" : "me/"}gigs?` +
        `pageIndex=${pageIndex}` +
        `&pageSize=${pageSize}` +
        `&sortOrder=${sortOrder}` +
        `&orderBy=${orderBy}` +
        (hiredTalentId ? `&hiredTalentId=${hiredTalentId}` : "") +
        (giggedClientId ? `&giggedClientId=${giggedClientId}` : "") +
        gigStatusQuery +
        searchTermQuery +
        skillsQuery +
        taggedQuery +
        expectedDurationsQuery +
        hasClientHistoryQuery +
        locationsQuery +
        localitiesQuery +
        deliveryTypesQuery +
        referenceNumberQuery +
        gigTitleQuery +
        createdByQuery +
        inviteCreatedByQuery +
        talentNameQuery +
        startDateQuery;

    const { data: apiCallData, ...apiCall } =
        useGiggedApiSWR<GigApiPaginatedData<ApiGigSummary>>(pauseFetching ? null : url);

    const [createGig, isCreatingGig] = useCreateGig();

    const gigs = apiCallData?.items ? apiCallData?.items.map(mapFromApiGigSummary) : [];

    const updateLocalGig = (gigId: string, updates: Partial<GigSummary>) => {
        if (!apiCallData) return;
        
        // Create new items array with the updated gig
        const updatedItems = apiCallData.items.map(item => 
            item.id === gigId 
                ? { ...item, ...updates } 
                : item
        );

        // Update the SWR cache optimistically
        apiCall.mutate(
            {
                ...apiCallData,
                items: updatedItems
            },
            false // false means don't revalidate with the server
        );
    };

    return {
        ...apiCall,
        ...apiCallData,
        pageIndex: apiCallData?.pageIndex ?? 0,
        pageSize: apiCallData?.pageSize ?? initialPageSize ?? 10,
        totalCount: apiCallData?.totalCount ?? 0,
        totalPages: apiCallData?.totalPages ?? 0,
        hasPreviousPage: apiCallData?.hasPreviousPage ?? false,
        hasNextPage: apiCallData?.hasNextPage ?? false,
        gigs,
        skillIds,
        gigTaggedUserIds,
        expectedDurationIds,
        searchTerm,
        referenceNumber,
        gigTitle,
        sortOrder,
        orderBy,
        hasClientHistory,
        locationIds,
        localityIds,
        deliveryTypeIds,
        createdBy,
        inviteCreatedBy,
        talentName,
        startDate,
        setHasClientHistory,
        setLocationIds,
        setLocalityIds,
        setSearchTerm,
        setReferenceNumber,
        setSkillIds,
        setTaggedIds,
        setExpectedDurationIds,
        setGigTitle,
        setDeliveryTypeIds,
        setCreatedBy,
        setInviteCreatedBy,
        setTalentName,
        setStartDate,
        setSortOrder,
        setOrderBy,
        setPageIndex,
        setPageSize,
        refetchGigs: apiCall.refetch,
        createGig: async (dto: CreateGigDto) => {
            const response = await createGig(dto);

            if (response.success) {
                apiCall.mutate();
            }
            
            return response;
        },
        isCreatingGig,
        updateLocalGig,
    };
};